import { Component, computed, ElementRef, inject, OnInit, Pipe, PipeTransform, signal, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, of, switchMap } from 'rxjs';
import { catchError, defaultIfEmpty, map } from 'rxjs/operators';

import { AppEvent, CoOrganiserDetail, EventListSubmission, EventRegistration } from './models';
import { draftForceSlotKey } from './draft-force-lookup';
import { EventService } from './event.service';
import { EventPackService } from './event-pack.service';
import { isUsableName, isValidContactEmail, registrationDetailsComplete } from './registration-details';
import { UserService } from '../global/userService/user-service';
import { RedirectService } from '../global/userService/redirect.service';
import { ImageService } from '../global/images/image.service';
import { ForceService } from '../forces';
import { DataLibrary, getValueFromTranslationSync } from '../global';
import { AlertService } from '../global/alert/alert';
import { InputAlert } from '../global/input-alert/input-alert';
import { ToastService } from '../global/toast/toast';
import { ForceCreationContextService } from '../global/force-creation-context';
import { ForceSubmitContextService } from '../global/force-submit-context';
import { Modal } from '../global/modal';

@Pipe({ name: 'visibleCoOrganisers', standalone: true })
export class VisibleCoOrganisersPipe implements PipeTransform {
    transform(details: CoOrganiserDetail[] | undefined): CoOrganiserDetail[] {
        return (details || []).filter((co) => !!co.displayName);
    }
}

@Component({
    standalone: false,
    selector: 'abs-event-detail',
    template: `
        @if (event$ | async; as event) {
            <abs-page-layout [pageTitle]="event.title">
                <div class="event-hero" [class.event-hero--no-image]="!event.imageId">
                    @if (event.imageId) {
                        <img [src]="imageService.url(event.imageId)" alt="Event banner" class="event-hero-img" />
                    }
                    <div class="event-hero-overlay">
                        <h1 class="event-hero-title">{{ event.title }}</h1>
                    </div>
                </div>

                <div class="event-key-info">
                    <div class="key-item">
                        <ion-icon name="calendar-outline"></ion-icon>
                        <span>
                            {{ event.startDate | date: 'd MMM y' }}
                            @if (event.endDate && event.endDate !== event.startDate) {
                                &nbsp;&ndash;&nbsp;{{ event.endDate | date: 'd MMM y' }}
                            }
                        </span>
                    </div>
                    <div class="key-item">
                        <ion-icon name="location-outline"></ion-icon>
                        <span>
                            @if (event.location.venueName) {
                                {{ event.location.venueName }}&nbsp;&mdash;&nbsp;
                            }
                            @if (event.location.city) {
                                {{ event.location.city }},&nbsp;
                            }
                            @if (event.location.state) {
                                {{ event.location.state }},&nbsp;
                            }
                            {{ event.location.countryCode | countryName }}
                        </span>
                    </div>
                    @if (event.ticketPrice != null && event.ticketPrice > 0) {
                        <div class="key-item">
                            <ion-icon name="pricetag-outline"></ion-icon>
                            <span>{{ event.ticketPrice }} {{ event.currency || '' }}</span>
                        </div>
                    }
                    @if (event.paymentInstructions) {
                        <div class="key-item">
                            <ion-icon name="card-outline"></ion-icon>
                            <abs-markdown [source]="event.paymentInstructions"></abs-markdown>
                        </div>
                    }

                    <!--
                        Share and (when there is one) the event pack, as icon buttons rather
                        than full labelled buttons in the action bar below — they are quick,
                        secondary actions anyone can take, not the main thing a visitor came
                        to do, so they sit with the rest of the event's metadata instead of
                        competing with Register/Event Day for attention.
                    -->
                    <div class="event-key-actions">
                        @if (event.eventPackFileId) {
                            <!-- download-outline, not document-outline: this opens a file the
                                 organiser attached, so the action is fetching it rather than
                                 reading a page. Labelled, since an icon alone did not say which
                                 of these two buttons did what. -->
                            <ion-button class="btn-sm" fill="clear" [href]="imageService.url(event.eventPackFileId)" target="_blank">
                                <ion-icon name="download-outline" slot="start"></ion-icon>
                                <span>{{ 'GLOBAL.EVENTS.DETAIL.EVENT_PACK' | translate }}</span>
                            </ion-button>
                        }
                        <!-- Only when the organiser has NOT attached their own: an uploaded pack is
                             the authored one and wins. Two buttons offering "the event pack" would
                             leave a reader guessing which is the real one.

                             Deliberately labelled the same as the uploaded pack and not marked
                             "generated" - that is how it was made, which is of no interest to the
                             person downloading it. Same reason it downloads on the first tap
                             rather than opening a preview: there is nothing here to configure. -->
                        @else if ((canEdit$ | async) || (myRegistration$ | async)) {
                            <ion-button class="btn-sm" fill="clear" [disabled]="packSaving()" (click)="downloadPack(event)">
                                @if (packSaving()) {
                                    <ion-spinner name="dots" slot="start"></ion-spinner>
                                } @else {
                                    <ion-icon name="download-outline" slot="start"></ion-icon>
                                }
                                <span>{{ 'GLOBAL.EVENTS.DETAIL.EVENT_PACK' | translate }}</span>
                            </ion-button>

                            <!-- The document is rendered so it can be exported; the export reads
                                 this element's DOM. Hidden rather than absent, because what is
                                 serialized has to exist. -->
                            <div class="event-pack event-pack-offscreen" #packContent>
                                <abs-event-pack-document [event]="event"></abs-event-pack-document>
                            </div>
                        }
                        <!-- share-social-outline is the three-node graph people read as "share";
                             share-outline renders as an arrow leaving a box, which reads as
                             "open elsewhere". -->
                        <ion-button class="btn-sm" fill="clear" (click)="shareEvent()">
                            <ion-icon name="share-social-outline" slot="start"></ion-icon>
                            <span>{{ 'GLOBAL.EVENTS.SHARE_BUTTON' | translate }}</span>
                        </ion-button>
                    </div>
                </div>

                <!--
                    Declared once and reused by the action bars, the registration status
                    section and the list slots below. The template previously piped the
                    same registration stream through the async pipe four separate times,
                    which is four subscriptions to one stream.
                -->
                @let myReg = myRegistration$ | async;

                <!--
                    Two bars, because these are two different audiences' actions and
                    mixing them made an organiser's admin tools compete with the one
                    thing a visitor came to do.

                    This first one is what almost everyone sees: register, or once
                    registered, the event-day link. Share and the event pack moved to the
                    metadata header above — see the icon buttons in .event-key-actions —
                    since they are quick secondary actions, not the main thing a visitor
                    came to do.

                    Destructive actions are deliberately absent from both bars —
                    withdrawing a registration or the event itself has no business in a
                    tight row where a mis-tap is easy, and withdrawing a registration
                    belongs beside the status card it refers to. Per-slot list actions
                    stay with their slot, where they mean something.
                -->
                <div class="event-actions">
                    @if (event.publishStatus === 'published') {
                        @if (myReg && myReg.status !== 'withdrawn' && myReg.status !== 'removed') {
                            @if (myReg.status === 'active') {
                                <ion-button [routerLink]="['day']">
                                    <ion-icon name="today-outline" slot="start"></ion-icon>
                                    <span>{{ 'GLOBAL.EVENTS.EVENT_DAY.LINK' | translate }}</span>
                                </ion-button>
                            }
                        } @else if (registering()) {
                            <ion-button disabled>
                                <ion-spinner name="crescent"></ion-spinner>
                            </ion-button>
                        } @else if (!event.requireInAppLists || isSubscriber()) {
                            <ion-button (click)="register(event)">
                                <ion-icon name="person-add-outline" slot="start"></ion-icon>
                                <span>{{ 'GLOBAL.EVENTS.REGISTER_BUTTON' | translate }}</span>
                            </ion-button>
                        }
                    }

                    <!--
                        Shown instead of the Register button when the organiser has opted into
                        requiring in-app lists and this visitor isn't a subscriber — surfaced here,
                        not behind a click, so it's visible before they even try, per the design:
                        this is the organiser's choice, not a general Warlord policy, and it's
                        resolved with one tap into the existing subscribe flow (which already
                        explains the 7-day free trial).
                    -->
                    @if (
                        event.publishStatus === 'published' &&
                        (!myReg || myReg.status === 'withdrawn' || myReg.status === 'removed') &&
                        event.requireInAppLists &&
                        !isSubscriber()
                    ) {
                        <ion-card color="warning" class="reg-card">
                            <ion-card-content class="reg-card-body">
                                <ion-icon name="lock-closed-outline"></ion-icon>
                                <div class="reg-status-text">
                                    {{ 'GLOBAL.EVENTS.REGISTRATION.REQUIRES_SUBSCRIPTION' | translate }}
                                    <p class="reg-card-notes">
                                        {{ 'GLOBAL.EVENTS.REGISTRATION.REQUIRES_SUBSCRIPTION_NOTE' | translate }}
                                    </p>
                                </div>
                            </ion-card-content>
                        </ion-card>
                        <ion-button [routerLink]="['/subscription/subscribe']">
                            <ion-icon name="star-outline" slot="start"></ion-icon>
                            <span>{{ 'GLOBAL.EVENTS.REGISTRATION.SUBSCRIBE_CTA' | translate }}</span>
                        </ion-button>
                    }
                </div>

                <!--
                    The organiser's own tools, on their own row and deliberately smaller.
                    Only a handful of people ever see this bar, and none of it is what a
                    visitor came for, so it should read as a toolbar sitting under the
                    page's real actions rather than as a second set of equals.
                -->
                @if (canEdit$ | async) {
                    <div class="event-actions event-actions--organiser">
                        @if (event.publishStatus === 'draft') {
                            <ion-button class="btn-sm" color="primary" (click)="submitForReview(event)" [disabled]="submittingForReview()">
                                <ion-icon name="send-outline" slot="start"></ion-icon>
                                <span>{{ 'GLOBAL.EVENTS.SUBMIT_FOR_REVIEW' | translate }}</span>
                            </ion-button>
                        }
                        <ion-button class="btn-sm" fill="outline" [routerLink]="['manage']">
                            <ion-icon name="settings-outline" slot="start"></ion-icon>
                            <span>{{ 'GLOBAL.EVENTS.MANAGE_BUTTON' | translate }}</span>
                        </ion-button>
                    </div>
                }

                @if (event.description) {
                    <div class="event-description">
                        <abs-markdown [source]="event.description"></abs-markdown>
                    </div>
                }

                <!--
                    Registration status only. The Register button itself lives in the
                    action bar at the top, so this section exists only when there is a
                    registration to report — an empty bordered box under the description
                    was what rendering it unconditionally used to produce.
                -->
                @if (event.publishStatus === 'published' && myReg) {
                    <div class="registration-section">
                        @if (myReg; as reg) {
                            @if (reg.status === 'withdrawn') {
                                <ion-card color="medium" class="reg-card">
                                    <ion-card-content class="reg-card-body">
                                        <ion-icon name="close-circle-outline"></ion-icon>
                                        <div class="reg-status-text">
                                            {{ 'GLOBAL.EVENTS.REGISTRATION.WITHDRAWN' | translate }}
                                            @if (reg.withdrawnAt) {
                                                <span class="reg-status-date">{{ reg.withdrawnAt | date: 'd MMM y' }}</span>
                                            }
                                        </div>
                                    </ion-card-content>
                                </ion-card>
                            } @else if (reg.status === 'removed') {
                                <ion-card color="danger" class="reg-card">
                                    <ion-card-content class="reg-card-body">
                                        <ion-icon name="remove-circle-outline"></ion-icon>
                                        <div class="reg-status-text">
                                            {{ 'GLOBAL.EVENTS.REGISTRATION.REMOVED' | translate }}
                                            @if (reg.removalNotes) {
                                                <p class="reg-card-notes">{{ reg.removalNotes }}</p>
                                            } @else {
                                                <p class="reg-card-notes">{{ 'GLOBAL.EVENTS.REGISTRATION.REMOVED_CONTACT' | translate }}</p>
                                            }
                                            @if (event.contactEmail) {
                                                <p class="reg-card-notes">
                                                    <a [href]="'mailto:' + event.contactEmail">{{ event.contactEmail }}</a>
                                                </p>
                                            }
                                        </div>
                                    </ion-card-content>
                                </ion-card>
                            } @else {
                                @if (!detailsComplete(reg)) {
                                    <ion-card color="warning" class="reg-card details-incomplete">
                                        <ion-card-content class="reg-card-body">
                                            <ion-icon name="alert-circle-outline"></ion-icon>
                                            <div class="reg-status-text">
                                                {{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_INCOMPLETE' | translate }}
                                                <p class="reg-card-notes">
                                                    {{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_INCOMPLETE_NOTE' | translate }}
                                                </p>
                                                <ion-button class="btn-sm" (click)="editDetails(reg)">
                                                    {{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_COMPLETE_CTA' | translate }}
                                                </ion-button>
                                            </div>
                                        </ion-card-content>
                                    </ion-card>
                                }
                                <div class="registration-status">
                                    @switch (reg.status) {
                                        @case ('active') {
                                            <!-- Deliberately NOT a filled success card, unlike every
                                                 other status below. Being registered is the expected
                                                 state and asks nothing of the reader, so a bold green
                                                 panel simply competed with Event Day, which is the
                                                 actual call to action at this point in the lifecycle.
                                                 The other statuses keep their cards because each of
                                                 them does need noticing. -->
                                            <div class="reg-confirmed">
                                                <!-- No color="success": Ionic's green is saturated enough
                                                     to pull the eye on a dark game background, which is
                                                     the whole thing this line is trying not to do. The
                                                     tick shape carries the meaning; inheriting the text
                                                     colour also keeps it legible on every game theme
                                                     rather than one tuned for K47. -->
                                                <ion-icon name="checkmark-circle"></ion-icon>
                                                <!-- REGISTERED_ON already begins with "Registered", so the
                                                     two keys together read "Registered Registered on ...".
                                                     Only one of them is ever shown. -->
                                                @if (reg.registeredAt) {
                                                    <span>{{
                                                        'GLOBAL.EVENTS.REGISTRATION.REGISTERED_ON'
                                                            | translate: { date: reg.registeredAt | date: 'd MMM y' }
                                                    }}</span>
                                                } @else {
                                                    <span>{{ 'GLOBAL.EVENTS.REGISTRATION.REGISTERED' | translate }}</span>
                                                }
                                            </div>
                                        }
                                        @case ('pending') {
                                            <ion-card color="warning" class="reg-card">
                                                <ion-card-content class="reg-card-body">
                                                    <ion-icon name="time-outline"></ion-icon>
                                                    <div class="reg-status-text">
                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.PENDING_APPROVAL' | translate }}
                                                        @if (reg.registeredAt) {
                                                            <span class="reg-status-date">{{
                                                                'GLOBAL.EVENTS.REGISTRATION.REGISTERED_ON'
                                                                    | translate: { date: reg.registeredAt | date: 'd MMM y' }
                                                            }}</span>
                                                        }
                                                    </div>
                                                </ion-card-content>
                                            </ion-card>
                                        }
                                        @case ('waitlist') {
                                            <ion-card color="tertiary" class="reg-card">
                                                <ion-card-content class="reg-card-body">
                                                    <ion-icon name="list-outline"></ion-icon>
                                                    <div class="reg-status-text">
                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.ON_WAITLIST' | translate }}
                                                        @if (reg.registeredAt) {
                                                            <span class="reg-status-date">{{
                                                                'GLOBAL.EVENTS.REGISTRATION.REGISTERED_ON'
                                                                    | translate: { date: reg.registeredAt | date: 'd MMM y' }
                                                            }}</span>
                                                        }
                                                    </div>
                                                </ion-card-content>
                                            </ion-card>
                                        }
                                    }

                                    <!-- One quiet row, not two stacked full-width links: these are
                                         occasional corrections to a registration, not things a
                                         visitor came to do. -->
                                    <div class="registration-actions">
                                        <ion-button class="btn-xs" fill="clear" (click)="editDetails(reg)">
                                            {{ 'GLOBAL.EVENTS.REGISTRATION.EDIT_DETAILS' | translate }}
                                        </ion-button>
                                        <ion-button class="btn-xs" fill="clear" color="danger" (click)="withdrawRegistration(event, reg)">
                                            {{ 'GLOBAL.EVENTS.REGISTRATION.WITHDRAW' | translate }}
                                        </ion-button>
                                    </div>
                                </div>
                            }
                        }
                    </div>
                }

                <!--
                    List Submission Section (separate from registration). Also shown, read-only,
                    for a 'removed' registration — a removal must not swallow evidence of what was
                    submitted; only 'active' gets the interactive submit/withdraw affordances below.
                -->
                @if (myReg; as reg) {
                    @if ((reg.status === 'active' || reg.status === 'removed') && (event.listSlots?.length ?? 0) > 0) {
                        <div class="list-submission-section">
                            <h3 class="section-title">
                                <ion-icon name="document-text-outline"></ion-icon>
                                {{ 'GLOBAL.EVENTS.LIST_SUBMISSION.TITLE' | translate }}
                            </h3>
                            @for (submission of reg.listSubmissions; track submission.slotLabel) {
                                <div class="list-slot">
                                    <div class="list-slot-header">
                                        <span class="list-slot-label">{{ submission.slotLabel }}</span>
                                        @switch (submission.status) {
                                            @case ('submitted') {
                                                <ion-badge color="success">{{
                                                    'GLOBAL.EVENTS.LIST_SUBMISSION.SUBMITTED' | translate
                                                }}</ion-badge>
                                            }
                                            @case ('rejected') {
                                                <ion-badge color="danger">{{
                                                    'GLOBAL.EVENTS.LIST_SUBMISSION.REJECTED' | translate
                                                }}</ion-badge>
                                            }
                                            @case ('not_submitted') {
                                                <ion-badge color="medium">{{
                                                    'GLOBAL.EVENTS.LIST_SUBMISSION.NOT_SUBMITTED' | translate
                                                }}</ion-badge>
                                            }
                                        }
                                    </div>
                                    @if (submission.toNotes) {
                                        <p class="list-slot-notes">{{ submission.toNotes }}</p>
                                    }
                                    @if (
                                        reg.status === 'active' &&
                                        (submission.status === 'not_submitted' || submission.status === 'rejected')
                                    ) {
                                        @if (draftForceForSlot(event, submission); as draft) {
                                            <ion-button
                                                class="btn-sm"
                                                fill="outline"
                                                [routerLink]="['/', event.gameId, 'forces', draft.id]"
                                            >
                                                <ion-icon name="create-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.CONTINUE_LIST' | translate }}</span>
                                            </ion-button>
                                        } @else if (isSubscriber()) {
                                            <ion-button class="btn-sm" fill="outline" (click)="createListForSlot(event, reg, submission)">
                                                <ion-icon name="add-circle-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.CREATE_LIST' | translate }}</span>
                                            </ion-button>
                                            <ion-button class="btn-sm" fill="outline" (click)="selectListForSlot(event, reg, submission)">
                                                <ion-icon name="list-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.SELECT_LIST' | translate }}</span>
                                            </ion-button>
                                        } @else {
                                            <ion-button class="btn-sm" fill="outline" (click)="submitTextList(event, reg, submission)">
                                                <ion-icon name="create-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.SUBMIT_TEXT_LIST' | translate }}</span>
                                            </ion-button>
                                            <ion-button class="btn-sm" fill="outline" [routerLink]="['/subscription/subscribe']">
                                                <ion-icon name="star-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.REGISTRATION.SUBSCRIBE_CTA' | translate }}</span>
                                            </ion-button>
                                            <p class="subscriber-upsell">
                                                {{ 'GLOBAL.EVENTS.LIST_SUBMISSION.SUBSCRIBER_UPSELL' | translate }}
                                            </p>
                                        }
                                    }
                                    <!-- Any status other than not_submitted: a rejected list must still show
                                         what was written, or the player is asked to fix something they cannot
                                         see, and an accepted one must not vanish either. -->
                                    @if (submission.status !== 'not_submitted' && submission.submissionKind === 'text') {
                                        <div class="list-slot-summary">
                                            <p class="text-submission-body">{{ submission.textSubmission }}</p>
                                        </div>
                                    }
                                    @if (submission.status !== 'not_submitted' && submission.forceId) {
                                        <div class="list-slot-summary">
                                            <span class="list-slot-name">{{ submission.snapshotData?.name }}</span>
                                            @if (submission.snapshotData?.faction) {
                                                <span class="list-slot-faction">{{
                                                    (factionNames$ | async)?.[submission.snapshotData.faction] ||
                                                        submission.snapshotData.faction
                                                }}</span>
                                            }
                                            @if (submission.snapshotData?.cost != null) {
                                                <span class="list-slot-cost">{{ submission.snapshotData.cost }} pts</span>
                                            }
                                        </div>
                                        <div class="list-slot-actions">
                                            <ion-button class="btn-sm" fill="outline" [routerLink]="['../../forces', submission.forceId]">
                                                <ion-icon name="eye-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.VIEW_LIST' | translate }}</span>
                                            </ion-button>
                                        </div>
                                    }
                                    @if (reg.status === 'active' && submission.status === 'submitted') {
                                        <div class="list-slot-actions">
                                            <ion-button
                                                class="btn-sm"
                                                fill="outline"
                                                color="danger"
                                                (click)="withdrawList(event, reg, submission)"
                                            >
                                                <ion-icon name="close-circle-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.WITHDRAW_LIST' | translate }}</span>
                                            </ion-button>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                }

                <div class="event-stats">
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.CAPACITY' | translate }}</span>
                        <span class="stat-value">
                            {{ event.capacity }}&nbsp;{{ 'GLOBAL.EVENTS.DETAIL.PLAYERS' | translate }}
                            @if (event.waitlistEnabled) {
                                <small class="stat-badge">{{ 'GLOBAL.EVENTS.DETAIL.WAITLIST_ENABLED' | translate }}</small>
                            }
                        </span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.ROUNDS' | translate }}</span>
                        <span class="stat-value">{{ event.numberOfRounds }}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.PAIRING_MODE' | translate }}</span>
                        <span class="stat-value">{{ event.pairingMode | pairingModeKey | translate }}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.REGISTRATION' | translate }}</span>
                        <span class="stat-value">
                            {{
                                (event.registrationApprovalMode === 'auto'
                                    ? 'GLOBAL.EVENTS.DETAIL.AUTO_ACCEPT'
                                    : 'GLOBAL.EVENTS.DETAIL.TO_APPROVAL'
                                ) | translate
                            }}
                        </span>
                    </div>
                    @if (event.listSlots?.length > 0) {
                        <div class="stat-card stat-card--wide">
                            <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.LIST_SLOTS' | translate }}</span>
                            <span class="stat-value">
                                @for (slot of event.listSlots; track slot.label) {
                                    <span class="slot-entry">
                                        <!-- A single unnamed slot needs no name at all — there's nothing
                                             else it could be, so it shows only its points. Once there is
                                             more than one, ListSlotLabelPipe falls back to "Default"
                                             rather than leaving a bare em-dash with nothing before it. -->
                                        @if (slot | listSlotLabel: event.listSlots.length; as label) {
                                            {{ label }}
                                            @if (slot.pointsLimit) {
                                                &mdash; {{ slot.pointsLimit }}&nbsp;pts
                                            }
                                        } @else if (slot.pointsLimit) {
                                            {{ slot.pointsLimit }}&nbsp;pts
                                        }
                                        @if (slot.required) {
                                            <small class="stat-badge">{{ 'GLOBAL.EVENTS.DETAIL.REQUIRED' | translate }}</small>
                                        }
                                    </span>
                                }
                            </span>
                        </div>
                    }
                    @if (event.listSubmissionDate) {
                        <div class="stat-card">
                            <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.LIST_SUBMISSION_DEADLINE' | translate }}</span>
                            <span class="stat-value">{{ event.listSubmissionDate | date: 'd MMM y' }}</span>
                        </div>
                    }
                </div>

                <!-- The organiser's own sections - parking, food, whatever they headed. Above the
                     schedule because they are things a player reads while deciding to come, where
                     the schedule is what they read once they have. -->
                @if (event.infoSections?.length) {
                    <ion-list class="event-info-sections" lines="full">
                        @for (section of event.infoSections; track $index) {
                            @if (section.heading || section.body) {
                                <ion-item>
                                    <ion-label class="ion-text-wrap">
                                        <h3>{{ section.heading }}</h3>
                                        @if (section.body) {
                                            <abs-markdown [source]="section.body"></abs-markdown>
                                        }
                                    </ion-label>
                                </ion-item>
                            }
                        }
                    </ion-list>
                }

                <!-- The day's plan, before the event rather than only on it: a player deciding
                     whether to come, or building a list, wants to know which scenarios are being
                     played. Same component as the event-day page, so tapping a block reveals the
                     organiser's note for it here too. -->
                @if (event.scheduleBlocks?.length) {
                    <abs-event-schedule
                        [heading]="'GLOBAL.EVENTS.DETAIL.SCHEDULE' | translate"
                        [blocks]="event.scheduleBlocks"
                    ></abs-event-schedule>
                }

                <ion-list class="event-secondary" lines="full">
                    @if (event.organiserDisplayName) {
                        <ion-item>
                            <ion-label>
                                <h3>{{ 'GLOBAL.EVENTS.DETAIL.ORGANISER' | translate }}</h3>
                                <p>{{ event.organiserDisplayName }}</p>
                            </ion-label>
                        </ion-item>
                    }
                    <!-- May be a private relay/alias the organiser set deliberately, rather than
                         their real account email — either way, this is the address to use. -->
                    @if (event.contactEmail) {
                        <ion-item>
                            <ion-label>
                                <h3>{{ 'GLOBAL.EVENTS.DETAIL.CONTACT' | translate }}</h3>
                                <p>
                                    <a [href]="'mailto:' + event.contactEmail">{{ event.contactEmail }}</a>
                                </p>
                            </ion-label>
                        </ion-item>
                    }
                    @let visibleCos = event.coOrganiserDetails | visibleCoOrganisers;
                    @if (visibleCos.length > 0) {
                        <ion-item>
                            <ion-label>
                                <h3>{{ 'GLOBAL.EVENTS.DETAIL.CO_ORGANISERS' | translate }}</h3>
                                @for (co of visibleCos; track co.userId) {
                                    <p>{{ co.displayName }}</p>
                                }
                            </ion-label>
                        </ion-item>
                    }
                    <!-- Visibility is an organiser's own setting for who can find the event,
                         not information a visitor looking at it needs. -->
                    @if (canEdit$ | async) {
                        <ion-item>
                            <ion-label>
                                <h3>{{ 'GLOBAL.EVENTS.DETAIL.VISIBILITY' | translate }}</h3>
                                <p>{{ event.visibility | visibilityKey | translate }}</p>
                            </ion-label>
                        </ion-item>
                    }
                </ion-list>

                <!--
                    What is left at the bottom is deliberately only the rare and the
                    destructive: review state an organiser reads rather than acts on,
                    and withdrawing the event itself. Everything an organiser actually
                    reaches for is in the action bar at the top.
                -->
                @if (canEdit$ | async) {
                    <div class="buttons">
                        @if (event.publishStatus === 'pending_review') {
                            <ion-badge color="warning">{{ 'GLOBAL.EVENTS.PENDING_REVIEW_BADGE' | translate }}</ion-badge>
                        }
                        @if (event.publishReview?.reviewNotes && event.publishStatus === 'draft' && event.publishReview?.reviewedAt) {
                            <p class="review-notes">
                                {{ 'GLOBAL.EVENTS.LAST_REVIEW_NOTES' | translate }}: {{ event.publishReview.reviewNotes }}
                            </p>
                        }
                        @if (event.publishStatus === 'pending_review' || event.publishStatus === 'published') {
                            <ion-button color="medium" fill="outline" (click)="withdrawEvent(event)" [disabled]="withdrawing()">
                                {{ 'GLOBAL.EVENTS.WITHDRAW_BUTTON' | translate }}
                            </ion-button>
                        }
                    </div>
                }

                <ng-template #registrationDetailsForm>
                    <abs-registration-details-form
                        [name]="detailsName"
                        [contactEmail]="detailsEmail"
                        (nameChange)="detailsName = $event"
                        (contactEmailChange)="detailsEmail = $event"
                        (validChange)="detailsValid = $event"
                    ></abs-registration-details-form>
                </ng-template>
                <ng-template #registrationDetailsButtons>
                    <ion-button [disabled]="!detailsValid" (click)="submitDetails()">
                        {{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_SAVE' | translate }}
                    </ion-button>
                </ng-template>
            </abs-page-layout>
        }
    `,
    styles: [
        `
            .event-hero {
                position: relative;
                width: 100%;
                height: 280px;
                overflow: hidden;
                margin-bottom: 0;
            }

            .event-hero--no-image {
                height: auto;
                min-height: 100px;
                background: var(--ion-color-dark, #222428);
            }

            .event-hero-img {
                width: 100%;
                height: 280px;
                object-fit: cover;
                display: block;
            }

            .event-hero-overlay {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: flex-end;
                padding: 20px 16px;
                background: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.15) 60%, transparent 100%);
            }

            .event-hero--no-image .event-hero-overlay {
                position: relative;
                background: none;
            }

            .event-hero-title {
                margin: 0;
                font-size: 1.5rem;
                font-weight: 700;
                line-height: 1.25;
                color: #fff;
                text-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
            }

            .event-key-info {
                display: flex;
                flex-direction: column;
                gap: 8px;
                padding: 16px;
                background: var(--ion-color-light, #f4f4f4);
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .key-item {
                display: flex;
                align-items: flex-start;
                gap: 10px;
                font-size: 0.95rem;
                line-height: 1.4;
            }

            .key-item ion-icon {
                font-size: 1.1rem;
                flex-shrink: 0;
                margin-top: 2px;
                color: var(--ion-color-medium, #92949c);
            }

            .event-description {
                padding: 16px;
                padding-top: 0;
                margin-top: 16px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .event-description p {
                margin: 0;
                font-size: 0.95rem;
                line-height: 1.6;
                color: var(--ion-color-dark, #222428);
            }

            .event-stats {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1px;
                background: var(--ion-color-light-shade, #d7d8da);
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .stat-card {
                display: flex;
                flex-direction: column;
                gap: 4px;
                padding: 14px;
                background: var(--ion-background-color, #fff);
            }

            .stat-card--wide {
                grid-column: 1 / -1;
            }

            .stat-label {
                font-size: 0.7rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.06em;
                color: var(--ion-color-medium, #92949c);
            }

            .stat-value {
                font-size: 0.95rem;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 6px;
            }

            .stat-badge {
                font-size: 0.7rem;
                padding: 2px 6px;
                border-radius: 4px;
                background: var(--ion-color-light-shade, #d7d8da);
                color: var(--ion-color-dark, #222428);
            }

            .slot-entry {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .event-secondary {
                margin-top: 0;
            }

            .buttons {
                padding: 16px;
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .registration-section {
                padding: 16px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .reg-card {
                margin: 0;
            }

            /* Slimmer than it was: this confirms a state rather than asking for anything,
               so it should read at a glance without taking the top of the page. */
            .reg-card-body {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: 600;
                font-size: 0.85rem;
                padding: 7px 12px;
            }

            .reg-card-body ion-icon {
                font-size: 1rem;
                flex-shrink: 0;
            }

            .registration-status {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }

            /* A single quiet line: colour carried by the tick alone, so the state still
               reads at a glance without a filled panel pulling the eye off the CTA. */
            .reg-confirmed {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 0.85rem;
                opacity: 0.8;
            }

            .reg-confirmed ion-icon {
                font-size: 1rem;
                flex-shrink: 0;
            }

            .registration-actions {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
            }

            .registration-actions ion-button {
                margin: 0;
            }

            .reg-status-text {
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .reg-status-date {
                font-size: 0.75rem;
                font-weight: 400;
                opacity: 0.7;
            }

            /* Deliberately no colour override — this sits inside a coloured .reg-card
               (danger/warning/etc), so it must inherit that card's own contrast text
               colour to stay readable, the same way the status heading above it does.
               .list-slot-notes' hardcoded danger-shade red is for a plain/light
               background (e.g. a rejected list's note) and unreadable here — reusing
               it on a red card produced literal red-on-red text. */
            .reg-card-notes {
                margin: 2px 0 0;
                font-size: 0.85rem;
                font-weight: 400;
                font-style: italic;
                opacity: 0.9;
            }

            /* Inherit the card's own contrast colour rather than the browser's default
               link blue, which would clash on a coloured card — underline is what marks
               it as a link instead. */
            .reg-card-notes a {
                color: inherit;
                text-decoration: underline;
            }

            .list-submission-section {
                padding: 16px;
                display: flex;
                flex-direction: column;
                gap: 10px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            /* margin: 0 because this is an h3 inside .pageContent, which the global stylesheet
               gives both a gutter side margin and a default vertical one. The side margin
               indented the heading past the slots below it, and the bottom margin ADDED to the
               section's 10px flex gap (gaps do not collapse with margins), which is what made
               the space before the first list look so large. The gap itself is kept - it is
               also what separates one slot from the next. */
            .list-submission-section .section-title {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
            }

            .list-submission-section .section-title ion-icon {
                font-size: 1.2rem;
                color: var(--ion-color-medium, #92949c);
            }

            .list-slot {
                display: flex;
                flex-direction: column;
                gap: 6px;
                padding: 10px;
                background: var(--ion-color-light, #f4f4f4);
                border-radius: 6px;
            }

            .list-slot-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .list-slot-label {
                font-weight: 600;
                font-size: 0.9rem;
            }

            .list-slot-notes {
                margin: 0;
                font-size: 0.85rem;
                color: var(--ion-color-danger-shade, #cf3c4f);
                font-style: italic;
            }

            .list-slot-summary {
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
                margin-bottom: 6px;
                font-size: 0.9rem;
            }

            .list-slot-name {
                font-weight: 600;
            }

            .list-slot-faction {
                color: var(--ion-color-medium, #92949c);
            }

            .list-slot-faction::before {
                content: '·';
                margin-right: 8px;
            }

            .list-slot-cost {
                color: var(--ion-color-medium, #92949c);
            }

            .list-slot-cost::before {
                content: '·';
                margin-right: 8px;
            }

            .list-slot-actions {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
            }

            .subscriber-upsell {
                margin: 4px 0 0;
                font-size: 0.8rem;
                color: var(--ion-color-medium, #92949c);
                font-style: italic;
            }

            .text-submission-body {
                margin: 0;
                font-size: 0.9rem;
                white-space: pre-wrap;
                word-break: break-word;
            }

            .section-title {
                margin: 0 0 12px;
                font-size: 1rem;
                font-weight: 700;
            }

            .force-picker-list {
                max-height: 50vh;
                overflow-y: auto;
            }

            .review-notes {
                margin: 0;
                font-size: 0.85rem;
                color: var(--ion-color-medium, #92949c);
                font-style: italic;
            }
        `
    ]
})
export class EventDetailPage implements OnInit {
    private eventService = inject(EventService);
    private packService = inject(EventPackService);
    private userService = inject(UserService);
    private redirectService = inject(RedirectService);
    private forceService = inject(ForceService);
    private alertService = inject(AlertService);
    private inputAlert = inject(InputAlert);
    private toastService = inject(ToastService);
    private translateService = inject(TranslateService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private forceCreationContext = inject(ForceCreationContextService);
    private forceSubmitContext = inject(ForceSubmitContextService);
    private modal = inject(Modal);

    imageService = inject(ImageService);

    @ViewChild('packContent') packContent?: ElementRef<HTMLElement>;
    readonly packSaving = signal(false);
    @ViewChild('registrationDetailsForm') registrationDetailsForm: TemplateRef<any>;
    @ViewChild('registrationDetailsButtons') registrationDetailsButtons: TemplateRef<any>;

    detailsName = '';
    detailsEmail = '';
    detailsValid = false;
    private detailsSave: (() => void) | null = null;

    // Optional: the event pages live under a game's route tree, which provides the
    // library — but an event reached outside one should still render the submission,
    // falling back to the stored faction key rather than failing to inject.
    private dataLibrary = inject(DataLibrary, { optional: true });

    /**
     * Faction key -> display name. A submission snapshot stores the force's faction
     * key (`BRIT_COM`), which is an internal identifier, not something to show a user.
     */
    readonly factionNames$: Observable<Record<string, string>> = (this.dataLibrary?.factions$ ?? of([])).pipe(
        map((factions) =>
            factions.reduce(
                (agg, faction) => ({ ...agg, [faction.key]: getValueFromTranslationSync(faction.name) }),
                {} as Record<string, string>
            )
        ),
        defaultIfEmpty({} as Record<string, string>)
    );

    event$: Observable<AppEvent>;
    canEdit$: Observable<boolean>;
    myRegistration$: Observable<EventRegistration | null>;

    registering = signal(false);
    submittingForReview = signal(false);
    withdrawing = signal(false);

    private static SUBSCRIBER_ROLES = ['app_subscriber', 'app_subscriber_annual', 'app_free_trial'];

    readonly isSubscriber = computed(() => {
        const roles = this.userService.user()?.roles ?? [];
        return roles.some((r) => EventDetailPage.SUBSCRIBER_ROLES.includes(r));
    });

    // `Force` is typed `any` here since this file is shared across every game and can't
    // import bolt-action's own extended `Force` type (see Task 14's identical constraint
    // in force-list.page.ts). Built once per render of ForceService.forces() rather than
    // re-scanned per slot — draftForceForSlot() below is then an O(1) Map lookup.
    private draftForcesBySlot = computed(() => {
        const map = new Map<string, any>();
        for (const force of (this.forceService.forces() ?? []) as any[]) {
            if (force.eventDraft) {
                map.set(draftForceSlotKey(force.eventDraft.eventId, force.eventDraft.slotId, force.eventDraft.slotLabel), force);
            }
        }
        return map;
    });

    private refreshRegistration$ = new BehaviorSubject<void>(undefined);
    private refreshEvent$ = new BehaviorSubject<void>(undefined);

    ngOnInit() {
        // A stale, abandoned Select List attempt from a previous visit to this (or another
        // event's) detail page must never leak into this one.
        this.forceSubmitContext.clear();
        // Same for an abandoned Create List attempt.
        this.forceCreationContext.consume();

        this.event$ = combineLatest([this.route.paramMap, this.refreshEvent$]).pipe(
            map(([params]) => params.get('eventId')),
            switchMap((id) => this.eventService.getEvent(id))
        );

        this.canEdit$ = combineLatest([this.event$, this.userService.login$]).pipe(
            map(([event, login]) => {
                const userId = login?.user?.userId;
                if (!userId) return false;

                const isOrganiser = event.organiserId === userId;
                const isCoOrganiser = event.coOrganisers?.includes(userId) || false;

                if (isCoOrganiser && !login?.user?.displayName) return false;

                return isOrganiser || isCoOrganiser;
            })
        );

        this.myRegistration$ = combineLatest([this.event$, this.userService.login$, this.refreshRegistration$]).pipe(
            switchMap(([event, login]) => {
                if (!login?.user?.userId || !event?._id) return of(null);
                return this.eventService.getMyRegistration(event._id).pipe(catchError(() => of(null)));
            })
        );
    }

    register(event: AppEvent) {
        const login = this.userService.loginDetails();
        if (!login?.user) {
            // The button is reachable by an anonymous visitor (event details are public), so a
            // tap here needs somewhere to go rather than silently doing nothing — simplest is to
            // send them to log in, same as the plain Login button elsewhere in the app. Recorded
            // so LoginPage brings them back here afterward instead of dropping them at /home —
            // they may already be registered by then and just need to see that, or can now tap
            // Register again.
            this.redirectService.setRedirectUrl(this.router.url);
            this.router.navigate(['/login']);
            return;
        }

        const user = login.user;
        // Never seeds the name from the email. That fallback is the defect this change
        // removes; reproducing it here would put it straight back.
        this.detailsName = user.displayName && isUsableName(user.displayName) ? user.displayName : '';
        this.detailsEmail = user.email || '';
        this.detailsValid = isUsableName(this.detailsName) && isValidContactEmail(this.detailsEmail);
        this.detailsSave = () => this.doRegister(event);

        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.DETAILS_TITLE'),
                contentTemplate: this.registrationDetailsForm,
                buttonsTemplate: this.registrationDetailsButtons
            }
        });
    }

    submitDetails() {
        // The modal dismisses itself on this click (ModalComponent's host listener), so any
        // failure has to be reported by the caller's own toast, not inside the modal.
        this.detailsSave?.();
        this.detailsSave = null;
    }

    private doRegister(event: AppEvent) {
        this.registering.set(true);
        const displayName = this.detailsName.trim();
        const contactEmail = this.detailsEmail.trim();

        this.eventService.register(event._id, { displayName, contactEmail }).subscribe({
            next: () => {
                this.registering.set(false);
                this.refreshRegistration$.next();
                this.setProfileNameIfMissing(displayName);
            },
            error: (err) => {
                this.registering.set(false);
                // Surfaces the server's own reason (e.g. "Event is full and waitlist is
                // not enabled") when it sent one, rather than a fixed message that would
                // hide genuinely different failures behind identical, unhelpful text.
                const message = err?.error?.message || this.translateService.instant('GLOBAL.EVENTS.REGISTER_FAILED');
                this.toastService.showToast('', message, { color: 'danger', duration: 4000 });
            }
        });
    }

    /**
     * So a user types their name once rather than on every event. Fire-and-forget on purpose:
     * the registration has already succeeded by this point, and a failure to also update the
     * profile must not be reported as a failed registration.
     */
    private setProfileNameIfMissing(displayName: string) {
        const current = this.userService.loginDetails()?.user?.displayName;
        if (isUsableName(current)) {
            return;
        }
        this.userService.updateDisplayName(displayName).subscribe({
            next: () => this.userService.refreshAccessToken(),
            error: () => {}
        });
    }

    withdrawRegistration(event: AppEvent, reg: EventRegistration) {
        this.eventService.updateRegistrationStatus(event._id, reg._id, 'withdrawn').subscribe(() => {
            this.refreshRegistration$.next();
        });
    }

    /**
     * Called from the template. Permitted despite the no-methods-in-templates rule because it
     * returns a primitive derived from its argument — no new object or array reference per
     * change-detection cycle, which is the thrash that rule exists to prevent. There is no
     * signal to hang this off: `reg` arrives from an async pipe inside the template.
     */
    detailsComplete(reg: EventRegistration): boolean {
        return registrationDetailsComplete(reg);
    }

    editDetails(reg: EventRegistration) {
        // Prefilled from the registration, not the profile: this is what the organiser
        // currently has, and it is what the registrant is being asked to correct.
        this.detailsName = isUsableName(reg.displayName) ? reg.displayName : '';
        this.detailsEmail = reg.contactEmail || this.userService.loginDetails()?.user?.email || '';
        this.detailsValid = isUsableName(this.detailsName) && isValidContactEmail(this.detailsEmail);
        this.detailsSave = () => this.doUpdateDetails(reg);

        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.DETAILS_TITLE'),
                contentTemplate: this.registrationDetailsForm,
                buttonsTemplate: this.registrationDetailsButtons
            }
        });
    }

    private doUpdateDetails(reg: EventRegistration) {
        // No profile write-through here, unlike registering: correcting one event's details
        // must not silently rewrite the user's global name.
        this.eventService
            .updateRegistrationDetails(reg.eventId, reg._id, {
                displayName: this.detailsName.trim(),
                contactEmail: this.detailsEmail.trim()
            })
            .subscribe({
                next: () => this.refreshRegistration$.next(),
                error: (err) => {
                    const message = err?.error?.message || this.translateService.instant('GLOBAL.EVENTS.REGISTRATION.DETAILS_SAVE_FAILED');
                    this.toastService.showToast('', message, { color: 'danger', duration: 4000 });
                }
            });
    }

    // window.location.href rather than building the URL from event.gameId/_id — this
    // component is shared across every app (warlord, fallout, antares, fww), each on
    // its own domain, and there's no per-app "web base URL" config to build from (only
    // apiBaseUrl, which can point at a different host in local dev). The page the
    // visitor is already looking at IS the shareable link.
    /**
     * Generates the event pack and hands it straight to the browser. No preview and no options -
     * see the button's own note.
     */
    async downloadPack(event: AppEvent) {
        const element = this.packContent?.nativeElement;
        if (!element || this.packSaving()) {
            return;
        }

        this.packSaving.set(true);
        await this.packService.download(element, event);
        this.packSaving.set(false);
    }

    async shareEvent() {
        try {
            await navigator.clipboard.writeText(window.location.href);
            this.toastService.showToast('', this.translateService.instant('GLOBAL.SHARE.URL.COPIED'), {
                color: 'success',
                duration: 3000
            });
        } catch (err) {
            this.toastService.showToast('', this.translateService.instant('GLOBAL.SHARE.URL.COPY_ERROR'), {
                color: 'danger',
                duration: 3000
            });
        }
    }

    submitForReview(event: AppEvent) {
        this.submittingForReview.set(true);
        this.eventService.submitForReview(event._id!).subscribe({
            next: () => {
                this.submittingForReview.set(false);
                this.refreshEvent$.next();
            },
            error: () => this.submittingForReview.set(false)
        });
    }

    async withdrawEvent(event: AppEvent) {
        const result = await this.alertService.showConfirm(
            this.translateService.instant('GLOBAL.EVENTS.WITHDRAW_CONFIRM_TITLE'),
            this.translateService.instant('GLOBAL.EVENTS.WITHDRAW_CONFIRM_MESSAGE'),
            {
                ok: this.translateService.instant('GLOBAL.EVENTS.WITHDRAW_BUTTON'),
                cancel: this.translateService.instant('GLOBAL.CANCEL')
            }
        );
        if (result.role !== 'ok') {
            return;
        }
        this.withdrawing.set(true);
        this.eventService.withdrawEvent(event._id!).subscribe({
            next: () => {
                this.withdrawing.set(false);
                this.refreshEvent$.next();
                this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.WITHDRAW_SUCCESS'), {
                    color: 'success',
                    duration: 3000
                });
            },
            error: () => {
                this.withdrawing.set(false);
                this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.WITHDRAW_FAILED'), {
                    color: 'danger',
                    duration: 3000
                });
            }
        });
    }

    draftForceForSlot(event: AppEvent, submission: EventListSubmission) {
        return this.draftForcesBySlot().get(draftForceSlotKey(event._id!, submission.slotId, submission.slotLabel));
    }

    createListForSlot(event: AppEvent, reg: EventRegistration, submission: EventListSubmission) {
        const slot = event.listSlots?.find((s) =>
            submission.slotId != null ? s.id === submission.slotId : s.label === submission.slotLabel
        );
        this.forceCreationContext.set({
            eventId: event._id!,
            registrationId: reg._id!,
            slotId: submission.slotId,
            slotLabel: submission.slotLabel,
            pointsLimit: slot?.pointsLimit,
            orderDiceLimit: slot?.orderDiceLimit,
            // riftDiceLimit is K47-specific — the force-creation seeding this feeds sets
            // force.riftDiceLimit unconditionally whenever this context carries a value, so it
            // must stay absent for every other game (bolt-action-3e included) or those forces
            // would pick up a stray rift-dice limit that means nothing to them.
            riftDiceLimit: event.gameId === 'k47' ? (event.gameSpecificData as any)?.riftDiceLimit : undefined
        });
        this.router.navigate(['/', event.gameId, 'forces', 'new']);
    }

    selectListForSlot(event: AppEvent, reg: EventRegistration, submission: EventListSubmission) {
        this.forceSubmitContext.set({
            eventId: event._id!,
            eventTitle: event.title,
            registrationId: reg._id!,
            slotId: submission.slotId,
            slotLabel: submission.slotLabel
        });
        this.router.navigate(['/', event.gameId, 'forces']);
    }

    submitTextList(event: AppEvent, reg: EventRegistration, submission: EventListSubmission) {
        const message = this.translateService.instant('GLOBAL.EVENTS.LIST_SUBMISSION.SUBMIT_TEXT_LIST_PROMPT');
        // Seeded with whatever is already stored. Rejecting a list does not clear its text
        // server-side, so a player asked to fix one thing was being made to retype the whole
        // list from an empty box.
        const existing = submission.textSubmission ?? '';
        this.inputAlert.show(submission.slotLabel, message, existing, 'textarea', true).then((text) => {
            if (!text) {
                return;
            }
            this.submitText(event, reg, submission, text);
        });
    }

    private submitText(event: AppEvent, reg: EventRegistration, submission: EventListSubmission, text: string) {
        this.eventService
            .submitList(event._id!, reg._id!, {
                slotLabel: submission.slotLabel,
                slotId: submission.slotId,
                submissionKind: 'text',
                textSubmission: text
            })
            .subscribe({
                next: () => this.refreshRegistration$.next(),
                error: () => {
                    this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.LIST_SUBMISSION.SUBMIT_FAILED'), {
                        color: 'danger',
                        duration: 3000
                    });
                }
            });
    }

    withdrawList(event: AppEvent, reg: EventRegistration, submission: EventListSubmission) {
        this.eventService.withdrawList(event._id, reg._id, submission.slotLabel, submission.slotId).subscribe(() => {
            this.refreshRegistration$.next();
        });
    }
}
